import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Modal from './Modal';
import { useDocumentVersions, useRestoreDocumentVersion, useDownloadDocumentVersion } from '../../hooks/useQueries';
import { formatDateTime } from '../../utils/dateUtils';
import useAuthenticatedFetch from '../../hooks/useAuthenticatedFetch';
import API_CONFIG from '../../config/apiConfig';
import './VersionHistoryModal.css';

const API_BASE_URL = API_CONFIG.BASE_URL;
function apiUrl(path) {
  if (/^https?:\/\//.test(path)) return path;
  if (!API_BASE_URL) {
    return path.startsWith('/') ? path : '/' + path;
  }

  let base = API_BASE_URL.replace(/\/$/, '');
  if (process.env.NODE_ENV === 'development' && !/\/api$/i.test(base)) {
    base = base + '/api';
  }

  return base + (path.startsWith('/') ? path : '/' + path);
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  if (!bytes) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function VersionHistoryModal({ show, document, onCancel, onRestore }) {
  const documentId = document?.id;
  const { data, isLoading, error } = useDocumentVersions(documentId, { enabled: !!documentId && show });
  const restoreMutation = useRestoreDocumentVersion();
  const downloadVersion = useDownloadDocumentVersion();
  const authenticatedFetch = useAuthenticatedFetch();

  const [confirmRestore, setConfirmRestore] = useState({ show: false, version: null });
  const [actionError, setActionError] = useState(null);
  const [downloadingVersionId, setDownloadingVersionId] = useState(null);
  const [previewModal, setPreviewModal] = useState({ show: false, version: null, url: null, loading: false, error: null });

  const versions = useMemo(() => {
    const list = data?.versions || [];
    return [...list].sort((a, b) => (b.version_number || 0) - (a.version_number || 0));
  }, [data]);

  const closeConfirm = () => setConfirmRestore({ show: false, version: null });

  useEffect(() => {
    if (!show) {
      setActionError(null);
      setDownloadingVersionId(null);
      setConfirmRestore({ show: false, version: null });
    }
  }, [show]);

  useEffect(() => {
    return () => {
      if (previewModal.url) {
        URL.revokeObjectURL(previewModal.url);
      }
    };
  }, [previewModal.url]);

  const isPreviewable = (mimeType) => {
    if (!mimeType) return false;
    return mimeType.startsWith('image/') || mimeType === 'application/pdf';
  };

  const handleDownload = useCallback(
    async (version) => {
      if (!documentId || !version?.id) return;
      setActionError(null);
      setDownloadingVersionId(version.id);
      try {
        await downloadVersion(documentId, version.id, document?.filename, version.version_number);
      } catch (e) {
        setActionError(e?.message || 'Failed to download version');
      } finally {
        setDownloadingVersionId(null);
      }
    },
    [downloadVersion, documentId, document?.filename]
  );

  const handlePreview = useCallback(
    async (version) => {
      if (!documentId || !version?.id) return;
      setActionError(null);

      if (previewModal.url) {
        URL.revokeObjectURL(previewModal.url);
      }

      setPreviewModal({ show: true, version, url: null, loading: true, error: null });
      try {
        const response = await authenticatedFetch(apiUrl(`/documents/${documentId}/versions/${version.id}/download`));
        if (!response.ok) {
          throw new Error(`Preview failed: ${response.status}`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewModal({ show: true, version, url, loading: false, error: null });
      } catch (e) {
        setPreviewModal({ show: true, version, url: null, loading: false, error: e?.message || 'Failed to preview version' });
      }
    },
    [authenticatedFetch, documentId, previewModal.url]
  );

  const handleRestoreClick = (version) => {
    setConfirmRestore({ show: true, version });
  };

  const handleConfirmRestore = async () => {
    const version = confirmRestore.version;
    if (!documentId || !version?.id) return;

    setActionError(null);
    try {
      await restoreMutation.mutateAsync({
        documentId,
        versionId: version.id,
      });

      closeConfirm();
      if (onRestore) onRestore();
    } catch (e) {
      setActionError(e?.message || 'Failed to restore version');
    }
  };

  const mainContent = (
    <div>
      {actionError ? (
        <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>
          {actionError}
        </div>
      ) : null}
      {isLoading ? (
        <div className="version-loading">
          <div className="spinner-border text-primary" role="status" aria-label="Loading"></div>
          <div style={{ marginTop: '0.75rem' }}>Loading version history…</div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">
          Failed to load version history: {error.message || 'Unknown error'}
        </div>
      ) : versions.length === 0 ? (
        <div className="version-empty-state">
          <i className="bi bi-clock-history" style={{ fontSize: '1.5rem', color: '#64748b' }}></i>
          <div style={{ marginTop: '0.5rem', fontWeight: 600 }}>No version history available</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table version-history-table">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>Version</th>
                <th>Uploaded By</th>
                <th style={{ width: '190px' }}>Uploaded At</th>
                <th style={{ width: '120px' }}>File Size</th>
                <th style={{ width: '110px' }}>Status</th>
                <th style={{ width: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="version-history-row">
                  <td className="version-number-cell">v{v.version_number}</td>
                  <td>{v.uploaded_by || '—'}</td>
                  <td>{v.uploaded_at ? formatDateTime(v.uploaded_at) : '—'}</td>
                  <td>{formatFileSize(v.file_size)}</td>
                  <td>
                    {v.is_current ? <span className="current-badge">Current</span> : <span style={{ color: '#64748b' }}>—</span>}
                  </td>
                  <td>
                    <div className="version-actions">
                      {isPreviewable(v.mime_type) ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary version-action-btn"
                          title="Preview"
                          onClick={() => handlePreview(v)}
                          disabled={previewModal.loading}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary version-action-btn"
                        title="Download"
                        onClick={() => handleDownload(v)}
                        disabled={downloadingVersionId === v.id}
                      >
                        {downloadingVersionId === v.id ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-label="Downloading"></span>
                        ) : (
                          <i className="bi bi-download"></i>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary version-action-btn"
                        title="Restore"
                        disabled={!!v.is_current || restoreMutation.isPending}
                        onClick={() => handleRestoreClick(v)}
                      >
                        <i className="bi bi-arrow-counterclockwise"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal
        show={show}
        title="Version History"
        onCancel={onCancel}
        confirmText={null}
        cancelText={null}
        size="lg"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={restoreMutation.isPending}
              style={{ fontWeight: '500', padding: '0.75rem 1.25rem', borderRadius: '8px', fontSize: '0.875rem' }}
            >
              Close
            </button>
          </div>
        }
        content={mainContent}
      />

      <Modal
        show={previewModal.show}
        title={previewModal.version ? `Preview v${previewModal.version.version_number}` : 'Preview'}
        onCancel={() => {
          if (previewModal.url) {
            URL.revokeObjectURL(previewModal.url);
          }
          setPreviewModal({ show: false, version: null, url: null, loading: false, error: null });
        }}
        confirmText={null}
        cancelText={null}
        size="lg"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', width: '100%' }}>
            {previewModal.version ? (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleDownload(previewModal.version)}
                disabled={downloadingVersionId === previewModal.version.id || previewModal.loading}
              >
                <i className="bi bi-download"></i>
                Download
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                if (previewModal.url) {
                  URL.revokeObjectURL(previewModal.url);
                }
                setPreviewModal({ show: false, version: null, url: null, loading: false, error: null });
              }}
            >
              Close
            </button>
          </div>
        }
        content={
          <div className="version-preview">
            {previewModal.loading ? (
              <div className="version-loading">
                <div className="spinner-border text-primary" role="status" aria-label="Loading"></div>
                <div style={{ marginTop: '0.75rem' }}>Loading preview…</div>
              </div>
            ) : previewModal.error ? (
              <div className="alert alert-danger">{previewModal.error}</div>
            ) : previewModal.url && previewModal.version ? (
              previewModal.version.mime_type?.startsWith('image/') ? (
                <img src={previewModal.url} alt={`Preview v${previewModal.version.version_number}`} style={{ maxWidth: '100%', height: 'auto' }} />
              ) : previewModal.version.mime_type === 'application/pdf' ? (
                <embed src={previewModal.url} type="application/pdf" width="100%" height="600px" />
              ) : (
                <div className="alert alert-info">Preview not available for this file type.</div>
              )
            ) : null}
          </div>
        }
      />

      <Modal
        show={confirmRestore.show}
        title="Confirm Restore"
        onCancel={closeConfirm}
        onConfirm={handleConfirmRestore}
        confirmText={restoreMutation.isPending ? 'Restoring…' : 'Confirm'}
        cancelText="Cancel"
        confirmBtnClass="btn-danger"
        loading={restoreMutation.isPending}
        content={
          <div className="restore-warning">
            <div className="alert alert-warning" style={{ marginBottom: 0 }}>
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Are you sure you want to restore version <strong>v{confirmRestore.version?.version_number}</strong>? This will make it the current version.
            </div>
          </div>
        }
      />
    </>
  );
}
